import { MousePointer2, MousePointerBan } from 'lucide-react';
import { AnimatePresence, motion, useSpring, useTransform } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Skeleton } from 'ui';
import { Avatar } from 'ui/components/Avatar';
import Button from 'ui/components/Button';
import DropdownMenu from 'ui/components/Dropdown';
import { cn } from 'utils/cn';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getActiveUsers } from '../../../store/siteInfoSlice';
import { getCursorsVisible, getIsBottomBarLoading, setCursorsVisible } from '../../../store/uiSlice';
import { getUser } from '../../../store/userSlice';
import { Collaborator } from './collaborator';

export const Collaborators = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useSelector(getUser);
  const activeUsers = useAppSelector(getActiveUsers);
  const cursorsVisible = useAppSelector(getCursorsVisible);
  const isOnline = activeUsers[user.id] ? 'online' : 'offline';
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);


  return (
    <>
      {isBottomBarLoading ? (
        <Skeleton className="h-9 w-9 rounded-md" />
      ) : (
        <DropdownMenu modal={false} open={open} onOpenChange={(open) => setOpen(open)}>
          <DropdownMenu.Trigger
            className="rounded-md focus:outline-none focus:ring-1 hidden md:flex"
            asChild
          >
            <Button
              className={cn("overflow-visible w-fit p-0")}
              variant="ghost"
              tooltip={t('bottom_bar.collaborators_tooltip', 'View project team')}
              tooltipSide="top"
              toolTipPortal={true}
            >
              <div className='flex items-center gap-1 bg-ata-purple p-0.5 rounded-lg'>
                <div className={cn('relative rounded-md border-2 border-white dark:border-card')}>
                  {isOnline && <div className='absolute -bottom-[2px] -right-[2px] h-3 w-3 border-2 border-white dark:border-card bg-ata-green rounded-full z-20'></div>}
                  {!isOnline && <div className='absolute -bottom-[2px] -right-[2px] h-3 w-3 border-2 border-white dark:border-card bg-gray-400 rounded-full z-20'></div>}
                  <Avatar
                    src={user.avatar || ''}
                    alt={user.name}
                    id={user.normal_id}
                    className='size-[30px]'
                    title={user.name}
                    email={user.email}
                  />
                </div>
                <AnimatePresence mode='popLayout'>
                  {Object.keys(activeUsers).length > 1 && (
                    <motion.span
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className='text-sm text-white pr-2'
                    >
                      <span>+</span><AnimatedNumber value={Object.keys(activeUsers).length - 1} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content data-atarim portal={true} className="w-[250px] flex flex-col gap-2 pointer-events-auto"
            container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
          >
            <div className='max-h-[350px] overflow-y-auto'>
              {Object.keys(activeUsers).map((u) => (
                <DropdownMenu.Item className="px-1.5 focus:bg-transparent cursor-default" key={u}>
                  <Collaborator {...activeUsers[u]} />
                </DropdownMenu.Item>
              ))}
            </div>
            <div className='flex flex-col items-center gap-2 w-full'>
              <Button
                variant="secondary"
                size="sm"
                className={cn("max-w-full w-full text-sm", !cursorsVisible && "bg-tertiary/5")}
                tooltip={t('bottom_bar.collaborators_tooltip_hide_cursors', 'Control the visibility of the cursors (Includes yours)')}
                tooltipSide='top'
                tooltipClassName='max-w-[200px] text-center'
                onClick={() => dispatch(setCursorsVisible(!cursorsVisible))}
                leftIcon={cursorsVisible ? <MousePointer2 size={16} /> : <MousePointerBan size={16} />}
              >
                <span className='text-sm'>{cursorsVisible ? 'Cursors Are Visible' : 'Cursors Are Hidden'}</span>
              </Button>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
    </>
  );
};


function AnimatedNumber({ value }: { value: number }) {
  const spring = useSpring(value, { mass: 0.8, stiffness: 75, damping: 15 });
  const display = useTransform(spring, (current) =>
    Math.round(current).toLocaleString()
  );

  useEffect(() => {
    spring.set(value);
  }, [spring, value]);

  return <motion.span>{display}</motion.span>;
}